// ─────────────────────────────────────────────────────────────
// HelpTab — two-column layout: sticky left nav + scrollable content.
// Three sections: Script Bridge Guide, Discover Studio Guide, MapsBridge API Ref.
// The intro video lives in the left sidebar as a "Watch the Intro" button
// that opens the shared VideoModal.
// ─────────────────────────────────────────────────────────────

import { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import { VideoModal } from "./VideoModal";
import type { OnboardingVideo } from "./VideoModal";

const LEFT_NAV_WIDTH = 220;
const MAPS_BRIDGE_VERSION = "v1.2.0";
const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

interface NavSection {
  id: string;
  label: string;
  children?: { id: string; label: string }[];
}

const SECTIONS: NavSection[] = [
  { id: "bridge-guide", label: "Script Bridge Guide" },
  {
    id: "helper-guide",
    label: "Discover Studio Guide",
    children: [
      { id: "helper-getting-started", label: "Getting started" },
      { id: "helper-marie", label: "Writing scripts with Marie" },
      { id: "helper-running", label: "Running your script" },
      { id: "helper-output", label: "Reading the output" },
      { id: "helper-versioning", label: "Saving and versioning" },
      { id: "helper-sharing", label: "Sharing with the community" },
      { id: "helper-community", label: "Browsing community scripts" },
      { id: "helper-images", label: "Working with images" },
      { id: "helper-troubleshooting", label: "When things go wrong" },
    ],
  },
  { id: "api-reference", label: "MapsBridge API Reference" },
];

// ── Shared pre-block style ────────────────────────────────────

const preStyle = {
  bgcolor: "background.default",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 0.5,
  p: 1.5,
  overflow: "auto",
  fontFamily: '"Cascadia Code", "Consolas", monospace',
  fontSize: "0.75rem",
  lineHeight: 1.6,
  my: 1.5,
} as const;

// ── Section content components ────────────────────────────────

function ScriptBridgeGuide() {
  return (
    <Box>
      <Typography variant="h2" gutterBottom>Script Bridge Guide</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Script Bridge is a Maps plugin that lets custom Python scripts run inside the Maps microscopy
        platform. Scripts communicate with Maps via stdin/stdout using JSON — the MapsBridge library
        handles all of this transparently.
      </Typography>

      <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>How Script Bridge Works</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        When Maps launches your script, it sends a JSON request over stdin describing the source layer
        and the work to perform. Your script reads this request using <code>MapsBridge</code>, processes
        images, and sends results back to Maps via stdout. All communication is handled by the library —
        you never write to stdin/stdout directly.
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        There are two types of source layer your script can target:
      </Typography>
      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Tile Sets</strong> — a grid of individually acquired tiles, each with image files and stage coordinates.</Typography></Box>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Image Layers</strong> — a single stitched or imported image positioned in the Maps project.</Typography></Box>
      </Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        Scripts can read image data, send processed images back to Maps, create annotations (sites or
        areas of interest), append notes to layers, attach files, and prepare new tile sets for
        acquisition.
      </Typography>

      <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Script Execution Modes</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Tile set scripts run in one of two modes, configured via the Default Parameters block or the
        Maps Script Bridge settings panel:
      </Typography>
      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
        <Box component="li">
          <Typography variant="body2" color="text.secondary">
            <strong>Single tiles</strong> (default) — Maps launches the script once per tile.
            <code>request.tiles_to_process</code> always contains exactly one tile. Progress is tracked
            automatically; do not call <code>report_progress()</code>.
          </Typography>
        </Box>
        <Box component="li">
          <Typography variant="body2" color="text.secondary">
            <strong>Batch</strong> — Maps launches the script once for the entire tile set.
            <code>request.tiles_to_process</code> contains all tiles. You must iterate over them and
            call <code>MapsBridge.report_progress()</code> to update the progress indicator.
          </Typography>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        Image layer scripts always run in single mode (one execution per layer). Execution mode
        does not apply to image layer scripts.
      </Typography>

      <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Script Run Triggers (Tile Sets)</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Tile set scripts can be triggered in several ways from Maps. Image layer scripts support
        manual triggering only.
      </Typography>
      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>manual</strong> — run on demand by the user.</Typography></Box>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>whencompleted</strong> — run automatically after tile set acquisition completes.</Typography></Box>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>whencompletedblocking</strong> — run after acquisition completes; blocks further processing until the script finishes.</Typography></Box>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>live</strong> — run after each tile is acquired.</Typography></Box>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>liveasync</strong> — run after each tile is acquired, without blocking acquisition of the next tile.</Typography></Box>
      </Box>

      <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Default Parameters Block</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Scripts can embed a default parameters comment block that Maps reads automatically when scanning
        the script folder. This pre-populates the Script Bridge settings panel for that script.
      </Typography>
      <Box component="pre" sx={preStyle}>
        {`# Default parameters
#{
#"RunMode" : "manual",
#"ScriptMode" : "singletiles",
#"ScriptParameters" : "120"
#}
# Default parameters end`}
      </Box>
      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>RunMode</strong>: <code>manual</code> | <code>whencompleted</code> | <code>whencompletedblocking</code> | <code>live</code> | <code>liveasync</code></Typography></Box>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>ScriptMode</strong>: <code>singletiles</code> | <code>batch</code></Typography></Box>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>ScriptParameters</strong>: any string — passed to the script as <code>request.script_parameters</code> exactly as written.</Typography></Box>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>PrepareImages</strong>: comma-separated channel indices (image layer scripts only) — tells Maps which channels to prepare before launching the script.</Typography></Box>
      </Box>

      <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Physical Units</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        All MapsBridge functions that accept physical measurements take a string with a numeric value
        and a unit. Supported formats:
      </Typography>
      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Length:</strong> <code>"1.3 um"</code>, <code>"200 nm"</code>, <code>"0.5 mm"</code>, <code>"3E-8 m"</code></Typography></Box>
        <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Angle:</strong> <code>"45 deg"</code>, <code>"1.3 rad"</code>, <code>"90 °"</code></Typography></Box>
      </Box>

      <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Output File Paths</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Never hardcode output paths like <code>/sandbox/output</code> or <code>C:\data</code>.
        Scripts written here run on real Maps workstations without modification — hardcoded paths
        will break in production.
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        For <strong>tile set scripts</strong>: get the output folder from the MapsBridge API after
        calling <code>get_or_create_output_tile_set()</code>:
      </Typography>
      <Box component="pre" sx={preStyle}>
        {`output_info = MapsBridge.get_or_create_output_tile_set("My Outputs")
output_folder = output_info.tile_set.data_folder_path  # always use this
output_path = os.path.join(output_folder, "result.tif")`}
      </Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        For <strong>image layer scripts</strong>: write output adjacent to the input file:
      </Typography>
      <Box component="pre" sx={preStyle}>
        {`input_path = request.prepared_images["0"]
output_folder = os.path.join(os.path.dirname(input_path), "script_output")
os.makedirs(output_folder, exist_ok=True)
output_path = os.path.join(output_folder, "result.png")`}
      </Box>

      <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Output Image Requirements</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        As of MapsBridge {MAPS_BRIDGE_VERSION}, the <strong>first image you send to a tile set is the
        anchor</strong>: it defines that tile set's pixel format, bit depth, <em>and</em> resolution.
        Maps automatically converts every later image to match the anchor — so your script never needs
        to pre-match formats between outputs.
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        <strong>Color is fully supported</strong> for both tile sets and image layers. The points below
        are about avoiding two specific surprises — distortion and a stitching limitation — not about
        avoiding color.
      </Typography>
      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
        <Box component="li">
          <Typography variant="body2" color="text.secondary">
            <strong>Match the anchor's aspect ratio.</strong> A later image whose aspect ratio differs
            from the tile set is resized nearest-neighbor to the anchor's exact W×H — it is
            <strong> stretched, not cropped or padded</strong>, which distorts the picture. Author every
            output at the anchor's aspect ratio.
          </Typography>
        </Box>
        <Box component="li">
          <Typography variant="body2" color="text.secondary">
            <strong>Even dimensions are best practice, not a gate.</strong> Keep width and height each
            <strong> divisible by 2</strong>. Maps doesn't reject odd dimensions in the Script Bridge —
            it silently resizes them, with the same stretch distortion above. So even dimensions plus a
            matching aspect ratio avoid distortion; they are not a validation requirement.
          </Typography>
        </Box>
        <Box component="li">
          <Typography variant="body2" color="text.secondary">
            <strong>Stitching needs a grayscale anchor.</strong> A tile set whose anchor image is color
            currently cannot be stitched. If a stitched mosaic matters, make the anchor grayscale
            (<code>Gray8</code> or <code>Gray16</code>) and emit color as a channel via
            {" "}<code>create_channel()</code>. If you don't need stitching, color is fine.
          </Typography>
        </Box>
        <Box component="li">
          <Typography variant="body2" color="text.secondary">
            <strong>Keep multiple outputs consistent.</strong> When a script emits several tile outputs,
            use the same pixel format and bit depth for all of them. Maps coerces mismatches to the
            anchor anyway, so matching up front keeps results predictable.
          </Typography>
        </Box>
      </Box>
      <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>RGB vs. channels</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Both produce color in the Maps Viewer, but they behave differently — pick based on how the
        result should display and whether it needs to stitch:
      </Typography>
      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
        <Box component="li">
          <Typography variant="body2" color="text.secondary">
            <strong>Bake into one RGB image</strong> for a fixed, authored result that must display
            exactly as you composed it. Trade-off: it is not user-re-tintable and not stitchable.
          </Typography>
        </Box>
        <Box component="li">
          <Typography variant="body2" color="text.secondary">
            <strong>Use multiple grayscale channels</strong> with a <code>create_channel()</code> color
            for signals that should be user-tintable, additive, or stitchable. Maps tints each grayscale
            channel by its channel color in the Viewer.
          </Typography>
        </Box>
        <Box component="li">
          <Typography variant="body2" color="text.secondary">
            <strong>RGBA overlays must use straight (non-premultiplied) alpha.</strong> Maps does not
            un-premultiply, so premultiplied alpha renders too dark. Save overlay PNGs with straight
            alpha.
          </Typography>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        Supported output pixel formats: <code>BlackWhite</code>, <code>Gray2</code>, <code>Gray4</code>,
        {" "}<code>Gray8</code>, <code>Gray16</code>, <code>Bgr24</code>, <code>Rgb24</code>,
        {" "}<code>Bgr32</code>, <code>Bgra32</code>, <code>Pbgra32</code>.
      </Typography>
    </Box>
  );
}

function HelperAppGuide() {
  return (
    <Box>
      <Typography variant="h2" gutterBottom>Discover Studio Guide</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Maps Discover Studio is a browser-based workspace for writing, testing, and sharing Python
        scripts that run on the Maps microscopy platform. This guide walks you through the things
        you'll actually want to do — start a script, get it running, save your work, share it, and
        recover when something goes sideways.
      </Typography>

      {/* ─ 1. Getting started ──────────────────────────────────── */}
      <Box id="helper-getting-started">
        <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Getting started — the fastest path</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          You have three ways to begin. Pick whichever feels right — there's no wrong order.
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li">
            <Typography variant="body2" color="text.secondary">
              <strong>Click a Starter Script.</strong> Open the <strong>Scripts</strong> tab and pick anything from
              the Starter Scripts grid (Gaussian Blur, Edge Detection, Particle Categorization, and so
              on). The editor loads with the script <em>and</em> a paired sample image already
              selected, so you can hit <strong>Run Script</strong> immediately and see real output. This is the
              easiest first try.
            </Typography>
          </Box>
          <Box component="li">
            <Typography variant="body2" color="text.secondary">
              <strong>Ask Marie to write one.</strong> Open the <strong>Python</strong> tab and tell Marie what you
              want — "Threshold this image at 50% and outline the largest blob in red." She'll write
              the script and drop it into the editor for you.
            </Typography>
          </Box>
          <Box component="li">
            <Typography variant="body2" color="text.secondary">
              <strong>Write code yourself.</strong> Open a blank script via <strong>New Script</strong> and use the
              Monaco editor directly. Marie is still there on the right if you need her to fill in a
              specific MapsBridge call.
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          New users with no saved scripts default to the Starter Scripts grid — you won't be staring
          at an empty tab.
        </Typography>
      </Box>

      {/* ─ 2. Writing scripts with Marie ───────────────────────── */}
      <Box id="helper-marie">
        <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Writing scripts with Marie</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Marie is the AI coding assistant in the right sidebar of the Python tab. She knows the
          complete MapsBridge API and writes scripts that run on real Maps workstations without
          modification.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Ask for what you actually want</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The more specific you are, the better the script. Tell Marie:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>What the input is</strong> ("a single SEM image", "a tile set of 6x4 BSE tiles").</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>What you want out</strong> ("a binary mask plus a CSV of particle areas in µm²").</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>What success looks like</strong> ("the largest grain should be circled in red").</Typography></Box>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Marie usually <strong>infers</strong> what she needs — input type, single-tile vs batch mode, output form —
          from your request and your loaded image, and just writes the script, <strong>stating any key
          assumption</strong> (such as whether the output is grayscale-and-stitchable or a fixed-color
          result) so you can correct it. She only asks a clarifying question in the rare case where
          it's genuinely ambiguous — most often <strong>Tile Set</strong> vs <strong>Image Layer</strong> — and when she does,
          clickable answer buttons appear under her question, no typing required.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Marie can see your code and your image</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          You don't have to copy and paste anything in. Whatever script is open in the editor goes
          to Marie automatically, and whatever image you currently have selected goes too — as
          actual pixels, not just metadata. A small <strong>"Image attached"</strong> chip appears above the chat
          input showing which image she can see. Click the X on the chip to detach it for one turn
          (useful if you want to ask a general question that isn't about the image); it re-arms on
          the next image pick.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Marie can comment on what's visible — drift artefacts, contrast issues, scale-bar
          position, particle distribution — which makes "look at the output, why isn't it what I
          expected?" a productive conversation rather than a guessing game.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Switching AI models</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The model picker at the top of Marie's chat lets you choose between:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Claude Opus 4.7</strong> (default) and <strong>Claude Opus 4.6</strong> — Anthropic.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>GPT-5.4</strong>, <strong>GPT-5-mini</strong>, and <strong>GPT-5</strong> — OpenAI.</Typography></Box>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          If one model feels slow or you get a "temporarily overloaded" message, switch to a model
          on a different provider — the two providers are independent of each other, so a Claude
          hiccup doesn't affect GPT-5. Switching between Claude variants (Opus 4.7 → 4.6) won't
          help with an Anthropic-wide capacity issue, though; switch to OpenAI instead.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Roll the editor back with Restore</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Every prior assistant message that produced code carries a <strong>↩ Restore</strong> chip. Click it,
          confirm, and the editor jumps back to that snapshot. The currently-active version is
          marked <strong>✓ Current</strong> instead, so you can always tell where you are. This is in-session
          only — for persistent saves between sessions, see {" "}
          <Box component="a" href="#helper-versioning" sx={{ color: "primary.main" }}>Saving and versioning</Box>.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Starting fresh</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Click <strong>Start new chat</strong> in Marie's header for a full reset: chat history clears, the
          editor empties, your selected image deselects, the loaded script unloads, and the
          parameter panel clears. One click, blank slate.
        </Typography>
      </Box>

      {/* ─ 3. Running your script ──────────────────────────────── */}
      <Box id="helper-running">
        <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Running your script</Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Pick an input image</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Almost every script needs one. In the left sidebar of the Python tab you have three ways
          to provide it:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Select from Library</strong> — opens a grid of every image you can see (your private uploads plus the shared library).</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Upload Temp</strong> — pick any TIFF/JPG/PNG from your local disk. It's used only for this run and isn't saved to your library.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary">A paired image loads automatically when you click a Starter Script or someone else's community script.</Typography></Box>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          The selected image shows as a thumbnail in the sidebar. Click the X to clear it; click
          the image to open the full-size viewer.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Hit Run, watch the console</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Click <strong>Run Script</strong> in the toolbar. Your script executes in a sandboxed Docker
          container. The console sits at the bottom of the editor view — same in Code mode and Chat
          mode — and shows everything your script printed plus any <code>MapsBridge.log_info()</code>
          messages.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The status indicator in the console header tells you what happened:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Green checkmark</strong> — success. The console auto-collapses so the output is front and centre, and the app switches to the Output tab.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Red error icon</strong> (hover for the exit code) — something failed. The console auto-expands so the error is the first thing you see.</Typography></Box>
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>When a script fails, Marie steps in</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          On failure, the app switches to Chat mode and Marie automatically reads stderr — separated
          from stdout, with the exit code, duration, and failure type — and proposes a corrected
          script. A <strong>Run Script</strong> chip appears beneath her reply so you can retry the fix in one
          click. You don't have to copy the traceback yourself.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>"Why won't Run do anything?"</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Almost always: you don't have an image selected. The image area in the left sidebar will
          flash orange three times and a Snackbar at the bottom of the screen will tell you. Click
          the orange placeholder to open the library, or use <strong>Upload Temp</strong>.
        </Typography>
      </Box>

      {/* ─ 4. Reading the output ───────────────────────────────── */}
      <Box id="helper-output">
        <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Reading the output</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          After a successful run, the Output tab lists every file your script produced. The input
          image is listed first (labelled <strong>Input</strong>) so you can flip back and forth between the
          original and the result.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Image outputs</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The viewer supports:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Mouse wheel</strong> to zoom (centred on the cursor).</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Click and drag</strong> to pan.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Double-click</strong> to reset zoom and pan.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Expand button</strong> in the toolbar for fullscreen.</Typography></Box>
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Reports, tables, and text</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The viewer is type-aware:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>PDF</strong> — embeds inline. Scroll within the viewer.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>CSV</strong> — renders as a searchable data table with sortable columns. Toggle <strong>View raw</strong> to see the underlying text.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>XLSX</strong> — shows a download card (the viewer doesn't render Excel files inline).</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>TXT, JSON, Markdown</strong> — monospace text viewer with JSON pretty-printed.</Typography></Box>
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Interactive Maps overlays</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          When your script calls <code>create_tile_set()</code>, <code>create_annotation()</code>, or
          similar Maps-bound APIs, Discover Studio draws those shapes on top of your output images so you
          can see what would have been sent to Maps:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Rectangles</strong> — proposed tile sets.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Ellipses</strong> — areas of interest (AOIs).</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Points</strong> — sites of interest (markers).</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Polylines</strong> — paths or tile-set boundaries.</Typography></Box>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          A toggle on the image toolbar shows or hides the overlays; a legend in the corner names
          each shape. The toggle does not affect what your script does — overlays are pure
          visualization. The same script runs identically on a real Maps workstation.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Download anything</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Every output file has a <strong>Download</strong> button in the viewer toolbar — works for images,
          PDFs, CSVs, XLSX, anything.
        </Typography>
      </Box>

      {/* ─ 5. Saving and versioning ────────────────────────────── */}
      <Box id="helper-versioning">
        <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Saving and versioning your scripts</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Every saved script has named, immutable versions. You can switch between them, restore an
          older one, and publish a specific version to the community without worrying that future
          edits will silently change what other users see.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>The first save</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Click <strong>Save</strong> on a brand-new script and the Save As dialog opens. Fill in a name and
          description. The image you currently have selected is paired with the script automatically
          — the dialog shows you a thumbnail of which image with a <strong>Change</strong> button if you want
          to swap it for another, or a <strong>Remove</strong> button if you'd rather not pair an image at all.
          That paired image is what loads alongside your script next time you (or anyone you share
          it with) opens it.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Later saves: two clear choices</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          From the second save onwards, the Save dialog offers two options:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li">
            <Typography variant="body2" color="text.secondary">
              <strong>Save changes to v3 (current)</strong> — overwrites the current version in place. Use this
              for small fixes you don't need to track separately.
            </Typography>
          </Box>
          <Box component="li">
            <Typography variant="body2" color="text.secondary">
              <strong>Create new v4 with a note</strong> — keeps v3 intact and adds a new version with an
              optional note like "added thermal colormap support". Use this when you want to keep a
              checkpoint you can roll back to.
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Older versions are immutable — editing v2 and saving doesn't overwrite v2; the changes go
          to head or create a new version.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Switching between versions</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          A <strong>version dropdown</strong> appears in the editor toolbar above Monaco when you have a saved
          script loaded. It lists every version with its note, and a small pin icon marks whichever
          version is currently published to the community (if any). Pick any version → its code
          loads.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          When you're viewing an older version, a clear <strong>"Viewing v2 (not current)"</strong> chip appears
          next to a <strong>Restore v2 as current</strong> button — one click promotes v2 to be the new head.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The <strong>Load ▾</strong> chevron menu next to the New Script button is a second surface for the
          same data — handy if you prefer loading from the sidebar instead of the toolbar.
        </Typography>
      </Box>

      {/* ─ 6. Sharing with the community ───────────────────────── */}
      <Box id="helper-sharing">
        <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Sharing with the community</Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Publishing a script</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Open the <strong>Scripts</strong> tab, find your script in <strong>My Scripts</strong>, and click <strong>Share</strong>. The
          publish dialog opens with your script's currently-paired image pre-filled — same thumbnail
          row as the Save dialog, with a single <strong>Change</strong> button if you'd like to swap the
          reference image, or a <strong>Remove</strong> button to publish without one.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          What other users will see on the Community tab: your title, description, paired image, and
          the <strong>exact version of code you published</strong>. The version pin in your editor toolbar shows
          you which one that is.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Edits are split between live and pinned</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The pencil <strong>Edit</strong> icon (on your own My Scripts cards and on your own Community cards)
          opens a dialog where you can change the title, description, or paired image. Those edits
          go live immediately — the moment you save, every other user sees the updated title,
          description, and image on the Community tab.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The <strong>code itself stays pinned</strong> to whichever version you published. If you want to
          publish new code, save a new version (see {" "}
          <Box component="a" href="#helper-versioning" sx={{ color: "primary.main" }}>Saving and versioning</Box>)
          and re-publish through the Share button.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Forking someone else's script</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          On any community card, click <strong>Fork</strong> to copy that script to your private My Scripts as
          <em> "&lt;title&gt; (forked)"</em>. Your fork is independent — modifying it doesn't touch the
          original, and the original author can keep publishing updates without affecting your copy.
        </Typography>
      </Box>

      {/* ─ 7. Browsing community scripts ───────────────────────── */}
      <Box id="helper-community">
        <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Browsing community scripts</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The Community sub-tab of the Scripts tab is where everyone's shared scripts live. The
          default view has three lanes:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Trending</strong> — the top-rated scripts overall. The top-rated card shows a small trophy icon.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Recently Shared</strong> — the three newest scripts.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>All Scripts</strong> — the full grid.</Typography></Box>
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Searching and sorting</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The search bar at the top of the Community sub-tab filters the All Scripts grid as you
          type — typing anything also hides the Trending and Recently Shared lanes so you can focus
          on results. Clear the search to bring the lanes back. The sort dropdown lets you order
          All Scripts by <strong>Popularity</strong>, <strong>Newest</strong>, or <strong>Highest rated</strong>.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Rating and reviewing</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          You can rate any script you don't own from 1 to 5 stars, with an optional review note up
          to 280 characters. One rating per user per script — submitting a new rating overwrites
          your previous one. The <strong>Reviews (N)</strong> chevron below each card expands a reviews
          panel. Your own review is highlighted; click <strong>Edit</strong> on it to revise.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Loading a community script</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Click any community card to load that script into the editor. Marie posts a one-or-two
          sentence intro into the chat naming the author — so you start with context, not a cold
          file. A <strong>Run Script</strong> chip under Marie's message lets you try the script
          immediately.
        </Typography>
      </Box>

      {/* ─ 8. Working with images ──────────────────────────────── */}
      <Box id="helper-images">
        <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>Working with images</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The Image Library tab has two sub-tabs:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>My Images</strong> — your private uploads.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Shared</strong> — everything visible to all users: the curated sample microscope images plus any uploads other users have shared.</Typography></Box>
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Uploading</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Drag a file onto the upload zone or click <strong>Upload</strong>. TIFF, JPG, and PNG are accepted up
          to 50 MB. Give the image a name and a description (at least 10 characters). You don't
          need to pick an image type by hand — modality (SEM / TEM / SDB / Optical) is inferred
          automatically.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          If your TIFF has FEI metadata tags (most Thermo Fisher scope outputs do), Discover Studio
          extracts instrument, accelerating voltage, magnification, pixel size, and detector signal
          automatically. Those values are visible as a chip on the image card and feed into Marie's
          context when you run a measurement script. You can manually edit any of them via the
          pencil icon on the metadata chip if the auto-extraction got something wrong.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Filtering by auto-derived tags</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Each image card shows a row of derived search tags:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Modality</strong> — SEM, TEM, SDB, Optical.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Magnification</strong> — Low-mag, Mid-mag, or High-mag (bucketed from the image's horizontal field width).</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Detector signal</strong> — what the image was captured with (BSE, SE, ETD, etc.).</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Calibration status</strong> — known pixel size or not.</Typography></Box>
          <Box component="li"><Typography variant="body2" color="text.secondary"><strong>Instrument family</strong> — Helios, Verios, Apreo, and so on.</Typography></Box>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          The filter chip row above the grid lets you narrow down — useful when you're looking for
          "an SEM image with a known pixel size" before running a measurement script.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>The green check on a card</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          A small green check means the image has a <strong>known pixel size</strong> — required for scripts that
          report measurements in physical units (µm, nm). If you write a script that calls
          <code>pixel_size</code> and the image doesn't have one, Marie's measurement helper will fall
          back to pixels and let you know.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Sharing your images</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Toggle the <strong>Share with everyone</strong> switch on the Upload dialog to share at upload time, or
          use the share toggle on an existing card later. Shared images appear in the Shared
          sub-tab for all users; un-sharing removes them again. Other users can save a private copy
          of any shared image to their My Images; their copy is independent of yours.
        </Typography>
      </Box>

      {/* ─ 9. When things go wrong ─────────────────────────────── */}
      <Box id="helper-troubleshooting">
        <Typography variant="h3" sx={{ mt: 2.5, mb: 1 }}>When things go wrong</Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>"[Model] is temporarily overloaded"</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The AI provider hit a capacity issue on their end — not us. The error bubble in Marie's
          chat names the model and provider so you can tell what's affected. Wait a minute and try
          again, or switch to a model on a <strong>different provider</strong> using the picker (Claude → GPT-5,
          for example). Switching between Claude variants doesn't help because it's Anthropic-wide;
          same for the OpenAI options when GPT is the one overloaded.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Script timeout</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The sandbox kills runs at 30 seconds. If you hit the limit, try a smaller crop, a simpler
          operation, or a smaller batch. For tile-set scripts, working on a single tile while you
          develop and only switching to batch mode at the end is usually the fastest path.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Run does nothing</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          You probably don't have an image selected. The image area in the left sidebar flashes
          orange three times and a Snackbar at the bottom of the screen tells you so. Click the
          orange placeholder to open the library picker, or use <strong>Upload Temp</strong> to grab a file from
          your disk.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>Marie's response was cut short</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Most often this is a token-budget issue — the script Marie was writing exceeded the
          per-response output limit. Try asking for a smaller change, or split your request into two
          messages ("first do the segmentation, then we'll add the CSV export"). If Marie tells you
          she ran out of tokens, that's exactly what happened.
        </Typography>
      </Box>
    </Box>
  );
}

// ── API Reference ─────────────────────────────────────────────

interface ApiEntry {
  name: string;
  returns: string;
  description: string;
  category: string;
}

const API_ENTRIES: ApiEntry[] = [
  // READING REQUESTS
  {
    category: "Reading Requests",
    name: "MapsBridge.ScriptTileSetRequest.from_stdin()",
    returns: "ScriptTileSetRequest",
    description: "Read the tile set request from Maps at script startup. Always call this first in tile set scripts.",
  },
  {
    category: "Reading Requests",
    name: "MapsBridge.ScriptImageLayerRequest.from_stdin()",
    returns: "ScriptImageLayerRequest",
    description: "Read the image layer request from Maps at script startup. Always call this first in image layer scripts.",
  },
  {
    category: "Reading Requests",
    name: "MapsBridge.read_request_from_stdin()",
    returns: "ScriptTileSetRequest | ScriptImageLayerRequest",
    description: "Auto-detects the request type and returns the correct object. Use when the script type is not known in advance.",
  },

  // REQUEST ATTRIBUTES — TILE SET
  {
    category: "Request Attributes — Tile Set",
    name: "request.source_tile_set",
    returns: "TileSetInfo",
    description: "The source tile set. Access .name, .guid, .column_count, .row_count, .data_folder_path, .tiles, .channels, .stage_position, .rotation, .horizontal_overlap, .vertical_overlap, and .pixel_to_stage_matrix.",
  },
  {
    category: "Request Attributes — Tile Set",
    name: "request.tiles_to_process",
    returns: "list[Tile]",
    description: "Tiles to process this execution. Contains one tile in single-tiles mode; all tiles in batch mode. Each Tile has .column and .row (1-based).",
  },
  {
    category: "Request Attributes — Tile Set",
    name: "request.script_parameters",
    returns: "str",
    description: "The raw parameter string from the Maps Script Bridge settings. Parse it yourself — e.g. float(request.script_parameters) or split on ';' for multiple values.",
  },

  // REQUEST ATTRIBUTES — IMAGE LAYER
  {
    category: "Request Attributes — Image Layer",
    name: "request.source_image_layer",
    returns: "ImageLayerInfo",
    description: "Source image layer info. Access .name, .guid, .stage_position, .rotation, .size, .total_layer_resolution, .pixel_to_stage_matrix, and .original_tile_set. In v1.2.0 ImageLayerInfo has NO .channels attribute — channels come from .original_tile_set.channels.",
  },
  {
    category: "Request Attributes — Image Layer",
    name: 'request.prepared_images["0"]',
    returns: "str",
    description: "Full path to the prepared input image for channel 0. Use this as your input file path.",
  },

  // TILE HELPERS
  {
    category: "Tile Helpers",
    name: "MapsBridge.get_tile_info(column, row, source_tile_set)",
    returns: "TileInfo | None",
    description: "Look up the full TileInfo for a tile by its 1-based column and row. Returns None if not found.",
  },
  {
    category: "Tile Helpers",
    name: "MapsBridge.tile_pixel_to_stage(pixel_x, pixel_y, tile_column, tile_row, tile_set)",
    returns: "PointFloat",
    description: "Convert pixel coordinates within a tile to physical stage coordinates in meters. Returns an object with .x and .y. The forward transform flips the Y axis — prefer this helper over indexing the matrix by hand.",
  },
  {
    category: "Tile Helpers",
    name: "MapsBridge.image_pixel_to_stage(pixel_x, pixel_y, image_layer)",
    returns: "PointFloat",
    description: "Convert pixel coordinates in an image layer to stage coordinates in meters. Uses the layer's pixel_to_stage_matrix; the forward transform flips the Y axis.",
  },
  {
    category: "Tile Helpers",
    name: "MapsBridge.calculate_total_pixel_position(pixel_x, pixel_y, tile_column, tile_row, tile_set)",
    returns: "PointInt",
    description: "Convert a pixel coordinate within a tile to its absolute pixel position in the full (stitched) tile-set image, accounting for tile overlap. Returns an object with integer .x and .y.",
  },
  {
    category: "Tile Helpers",
    name: "tile_set.pixel_to_stage_matrix / image_layer.pixel_to_stage_matrix",
    returns: "list[list[float]] (3×3)",
    description: "The pixel→stage transform as a 3×3 matrix [[a11,a12,a13],[a21,a22,a23],[a31,a32,a33]]: scale on the diagonal (m[0][0], m[1][1]), translation in the BOTTOM ROW (m[2][0], m[2][1]), and the forward transform flips the Y axis. Don't index it by hand — use tile_pixel_to_stage() / image_pixel_to_stage() instead.",
  },

  // TILEINFO ATTRIBUTES
  {
    category: "TileInfo Attributes",
    name: 'tile_info.image_file_names["0"]',
    returns: "str",
    description: "Filename (not full path) of the image for channel 0. Combine with source_tile_set.data_folder_path to build the full path.",
  },
  {
    category: "TileInfo Attributes",
    name: "tile_info.column, tile_info.row",
    returns: "int",
    description: "1-based tile column and row indices within the tile set grid.",
  },
  {
    category: "TileInfo Attributes",
    name: "tile_info.stage_position.x, .y",
    returns: "float",
    description: "Stage coordinates of this tile's center in meters.",
  },

  // FILENAME HELPERS
  {
    category: "Filename Helpers",
    name: "MapsBridge.get_tile_image_file_name(tile_row, tile_column, channel_index, plane_index, time_frame, extension=None, plugin_info=None)",
    returns: "str",
    description: "Build the canonical Maps tile image filename. As of v1.2.0 the extension defaults to .tiff when not supplied.",
  },
  {
    category: "Filename Helpers",
    name: "MapsBridge.get_tile_xt_image_file_name(tile_row, tile_column, channel_index, plane_index, time_frame, extension=None, slice=1, energy=0)",
    returns: "str",
    description: "Filename for an XT (slice/energy) tile image — adds the s####_e## plugin-info suffix. Defaults to .tiff.",
  },
  {
    category: "Filename Helpers",
    name: "MapsBridge.get_tile_eds_image_file_name(tile_row, tile_column, channel_index)",
    returns: "str",
    description: "Filename for an EDS tile image (plane 0, time-frame 0, .tiff extension).",
  },

  // OUTPUT — TILE SET
  {
    category: "Output — Tile Set",
    name: "MapsBridge.get_or_create_output_tile_set(tile_set_name, target_layer_group_name)",
    returns: "TileSetCreateInfo",
    description: "Get or create a read-only output tile set to receive script results. Use .tile_set.guid in subsequent send_single_tile_output and create_channel calls.",
  },
  {
    category: "Output — Tile Set",
    name: "MapsBridge.create_channel(channel_name, channel_color, is_additive, target_tile_set_guid)",
    returns: "Confirmation",
    description: "Create or update a channel in a tile set. channel_color is an RGB tuple, e.g. (255, 0, 0).",
  },
  {
    category: "Output — Tile Set",
    name: "MapsBridge.send_single_tile_output(tile_row, tile_column, target_channel_name, image_file_path, keep_file, target_tile_set_guid)",
    returns: "Confirmation",
    description: "Send a processed image back to Maps for the specified tile and channel. Also signals that processing for that tile is complete.",
  },

  // OUTPUT — IMAGE LAYER
  {
    category: "Output — Image Layer",
    name: "MapsBridge.create_image_layer(layer_name, image_file_path, target_layer_group_name, keep_file, align_to_source_layer)",
    returns: "ImageLayerCreateInfo",
    description: "Import an image file as a new Maps image layer. Result: .image_layer.guid.",
  },

  // OUTPUT — TILE SET CREATION
  {
    category: "Output — Tile Set Creation",
    name: "MapsBridge.create_tile_set(tile_set_name, stage_position, total_size, tile_hfw, pixel_size, schedule_acquisition, target_layer_group_name)",
    returns: "TileSetCreateInfo",
    description: "Create a new tile set in the Maps project and optionally schedule it for acquisition.",
  },
  {
    category: "Output — Tile Set Creation",
    name: "MapsBridge.create_tile_set_async(...)",
    returns: "None",
    description: "Fire-and-forget version of create_tile_set. Accepts the same arguments but does not wait for confirmation from Maps.",
  },

  // ANNOTATIONS
  {
    category: "Annotations",
    name: "MapsBridge.create_annotation(annotation_name, stage_position, rotation, size, notes, color, is_ellipse, target_layer_group_name)",
    returns: "AnnotationCreateInfo",
    description: "Create a site of interest (SOI) if size is None, or an area of interest (AOI) if size is provided. stage_position is a tuple (x, y, z) — rotation is a separate arg. Result: .annotation.guid.",
  },

  // NOTES & FILES
  {
    category: "Notes & Files",
    name: "MapsBridge.append_notes(notes, target_layer_guid)",
    returns: "Confirmation",
    description: "Append text notes to a Maps layer. Notes are cumulative and cannot be deleted by scripts.",
  },
  {
    category: "Notes & Files",
    name: "MapsBridge.store_file(file_path, overwrite, keep_file, target_layer_guid)",
    returns: "Confirmation",
    description: "Attach a file to a Maps layer. Note arg order: overwrite comes BEFORE keep_file. Executable files cannot be stored.",
  },

  // LAYER QUERY
  {
    category: "Layer Query",
    name: "MapsBridge.get_layer_info(layer_name, request_full_info)",
    returns: "LayerInfo",
    description: "Query a Maps layer by name. Returns .layer_exists, .guid, .name, .layer_type, and .layer_info.",
  },

  // LOGGING
  {
    category: "Logging",
    name: "MapsBridge.log_info(message)",
    returns: "Confirmation",
    description: "Log an informational message to the script logs.",
  },
  {
    category: "Logging",
    name: "MapsBridge.log_warning(message)",
    returns: "Confirmation",
    description: "Log a warning message to the script logs.",
  },
  {
    category: "Logging",
    name: "MapsBridge.log_error(message)",
    returns: "Confirmation",
    description: "Log an error message to the script logs.",
  },

  // PROGRESS & STATUS
  {
    category: "Progress & Status",
    name: "MapsBridge.report_progress(progress_percentage)",
    returns: "Confirmation",
    description: "Report script progress as a PERCENTAGE (0.0 to 100.0 — not a fraction). Use in batch mode only — single-tile mode tracks progress automatically.",
  },
  {
    category: "Progress & Status",
    name: "MapsBridge.report_activity_description(activity_description)",
    returns: "Confirmation",
    description: "Report a human-readable description of the current activity, visible in the Maps processing job list.",
  },
  {
    category: "Progress & Status",
    name: "MapsBridge.report_failure(error_message)",
    returns: "Confirmation",
    description: "Report an unrecoverable error. Terminates script processing immediately. Use for fatal errors only — not for per-tile recoverable issues.",
  },

  // STOP & EXIT
  {
    category: "Stop & Exit",
    name: "MapsBridge.MapsStopException",
    returns: "Exception",
    description: "Synchronous (confirmation-returning) MapsBridge calls may raise this when Maps requests a Stop. In batch scripts, let it propagate — don't swallow it — so processing halts cleanly.",
  },
  {
    category: "Stop & Exit",
    name: "MapsBridge.MapsExitException",
    returns: "Exception",
    description: "Synchronous MapsBridge calls may raise this when Maps requests an Exit. As with MapsStopException, batch scripts should let it propagate rather than catching it.",
  },
];

function ApiReferenceSection() {
  // Group entries by category, preserving insertion order
  const categories: string[] = [];
  const byCategory: Record<string, ApiEntry[]> = {};
  for (const entry of API_ENTRIES) {
    if (!byCategory[entry.category]) {
      categories.push(entry.category);
      byCategory[entry.category] = [];
    }
    byCategory[entry.category].push(entry);
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Typography variant="h2" sx={{ m: 0 }}>MapsBridge API Reference</Typography>
        <Chip size="small" label={MAPS_BRIDGE_VERSION} />
      </Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        Complete reference for all functions and attributes in the <code>MapsBridge</code> module.
        Import at the top of your script with <code>import MapsBridge</code>.
      </Typography>

      {categories.map((cat) => (
        <Box key={cat} sx={{ mt: 3 }}>
          <Typography
            variant="h3"
            sx={{ mb: 1.5, pb: 0.5, borderBottom: "1px solid", borderColor: "divider" }}
          >
            {cat}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {byCategory[cat].map((entry) => (
              <Box
                key={entry.name}
                sx={{
                  p: 1.5,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily: '"Cascadia Code", "Consolas", monospace',
                      fontSize: "0.8125rem",
                      color: "primary.main",
                    }}
                  >
                    {entry.name}
                  </Typography>
                  <Chip
                    label={`→ ${entry.returns}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.6875rem", fontFamily: '"Cascadia Code", "Consolas", monospace' }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {entry.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────

export function HelpTab() {
  const contentRef = useRef<HTMLDivElement>(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  // Which onboarding video the shared modal shows. The Help tab is a
  // permanent re-watch path for both videos — no gating here.
  const [activeVideo, setActiveVideo] = useState<OnboardingVideo>("welcome");

  function scrollToSection(id: string) {
    const el = contentRef.current?.querySelector(`#${id}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <>
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* ── Left nav ─────────────────────────────────────────── */}
      <Box
        sx={{
          width: LEFT_NAV_WIDTH,
          minWidth: LEFT_NAV_WIDTH,
          flexShrink: 0,
          borderRight: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          pt: 1.5,
          position: "sticky",
          top: 0,
          height: "100%",
          overflow: "auto",
        }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ px: 2, display: "block", mb: 0.5 }}>
          Contents
        </Typography>
        <List dense>
          {SECTIONS.map((s) => (
            <Box key={s.id}>
              <ListItemButton
                onClick={() => scrollToSection(s.id)}
                sx={{ py: 0.5 }}
              >
                <ListItemText
                  primary={s.label}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItemButton>
              {s.children?.map((child) => (
                <ListItemButton
                  key={child.id}
                  onClick={() => scrollToSection(child.id)}
                  sx={{ py: 0.25, pl: 3.5 }}
                >
                  <ListItemText
                    primary={child.label}
                    primaryTypographyProps={{
                      variant: "caption",
                      color: "text.secondary",
                    }}
                  />
                </ListItemButton>
              ))}
            </Box>
          ))}
        </List>
        <Divider sx={{ mt: 1, mb: 1.5 }} />
        <Box sx={{ px: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<PictureAsPdfIcon />}
            href={`${import.meta.env.BASE_URL}script-bridge-guide.pdf`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View PDF Guide
          </Button>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<DownloadIcon />}
            href={`${API_BASE}/api/mapsbridge/download`}
            sx={{
              mt: 1,
              py: 1,
              flexDirection: "column",
              alignItems: "center",
              lineHeight: 1.2,
              "& .MuiButton-startIcon": { mr: 0, mb: 0.25 },
            }}
          >
            <Box component="span" sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
              Download MapsBridge.py
            </Box>
            <Box component="span" sx={{ fontSize: "0.6875rem", opacity: 0.7, mt: 0.25 }}>
              {MAPS_BRIDGE_VERSION}
            </Box>
          </Button>
          {/*
            "Watch the Intro" — opens the shared VideoModal to the welcome
            video. Same outlined stacked-icon-above-label structure as the
            MapsBridge download button above so the sidebar reads as a
            parallel set of actions.
          */}
          <Button
            variant="outlined"
            fullWidth
            startIcon={<PlayCircleOutlinedIcon />}
            onClick={() => {
              setActiveVideo("welcome");
              setVideoModalOpen(true);
            }}
            aria-label="Watch the intro video"
            sx={{
              mt: 1,
              py: 1,
              flexDirection: "column",
              alignItems: "center",
              lineHeight: 1.2,
              "& .MuiButton-startIcon": { mr: 0, mb: 0.25 },
            }}
          >
            <Box component="span" sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
              Watch the Intro
            </Box>
            <Box component="span" sx={{ fontSize: "0.6875rem", opacity: 0.7, mt: 0.25 }}>
              2 min
            </Box>
          </Button>
          {/*
            "Watch the Quickstart" — mirror of the Intro button above,
            opens the shared VideoModal to the quickstart video. Permanent
            re-watch path; no onboarding gating on the Help tab.
          */}
          <Button
            variant="outlined"
            fullWidth
            startIcon={<PlayCircleOutlinedIcon />}
            onClick={() => {
              setActiveVideo("quickstart");
              setVideoModalOpen(true);
            }}
            aria-label="Watch the quickstart video"
            sx={{
              mt: 1,
              py: 1,
              flexDirection: "column",
              alignItems: "center",
              lineHeight: 1.2,
              "& .MuiButton-startIcon": { mr: 0, mb: 0.25 },
            }}
          >
            <Box component="span" sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
              Watch the Quickstart
            </Box>
            <Box component="span" sx={{ fontSize: "0.6875rem", opacity: 0.7, mt: 0.25 }}>
              3 min
            </Box>
          </Button>
        </Box>
      </Box>

      {/* ── Content area ─────────────────────────────────────── */}
      <Box
        ref={contentRef}
        sx={{
          flex: 1,
          overflow: "auto",
          px: 4,
          py: 3,
          maxWidth: 800,
        }}
      >
        <Box id="bridge-guide" sx={{ mb: 4 }}>
          <ScriptBridgeGuide />
        </Box>
        <Divider sx={{ mb: 4 }} />
        <Box id="helper-guide" sx={{ mb: 4 }}>
          <HelperAppGuide />
        </Box>
        <Divider sx={{ mb: 4 }} />
        <Box id="api-reference" sx={{ mb: 4 }}>
          <ApiReferenceSection />
        </Box>
      </Box>
    </Box>
    <VideoModal open={videoModalOpen} onClose={() => setVideoModalOpen(false)} which={activeVideo} />
    </>
  );
}
